/**
 * Admin Ticket List Page
 *
 * Admin view for managing all support tickets with advanced filtering and bulk operations
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAdminTickets,
  bulkUpdateTickets,
  getAvailableAdmins,
} from '@/services/tickets';
import type {
  Ticket,
  TicketFilters,
  TicketStatus,
  TicketCategory,
  TicketPriority,
  AvailableAdminsResponse,
} from '@/types';

const statusColors: Record<string, string> = {
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  waiting_user: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const priorityColors: Record<string, string> = {
  low: 'text-gray-600',
  medium: 'text-blue-600',
  high: 'text-orange-600',
  urgent: 'text-red-600',
};

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function AdminTicketListPage() {
  const { t } = useTranslation('admin');

  const statusLabels: Record<string, string> = {
    open: t('ticket_management.status.open'),
    in_progress: t('ticket_management.status.in_progress'),
    waiting_user: t('ticket_management.status.waiting_user'),
    resolved: t('ticket_management.status.resolved'),
    closed: t('ticket_management.status.closed'),
  };

  const categoryLabels: Record<string, string> = {
    technical: t('ticket_management.category.technical'),
    billing: t('ticket_management.category.billing'),
    feature_request: t('ticket_management.category.feature_request'),
    bug_report: t('ticket_management.category.bug_report'),
    account: t('ticket_management.category.account'),
    other: t('ticket_management.category.other'),
  };
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [admins, setAdmins] = useState<AvailableAdminsResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTickets, setSelectedTickets] = useState<number[]>([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const [filters, setFilters] = useState<TicketFilters>({
    status: undefined,
    category: undefined,
    priority: undefined,
    assigned_to: undefined,
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    per_page: 20,
    page: 1,
  });

  useEffect(() => {
    loadTickets();
    loadAdmins();
  }, [filters]);

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await getAdminTickets(filters);

      setTickets(response.data);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_management.messages.load_error'));
      console.error('Error loading tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const data = await getAvailableAdmins();
      setAdmins(data);
    } catch (err) {
      console.error('Error loading admins:', err);
    }
  };

  const handleFilterChange = (field: keyof TicketFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleSelectTicket = (ticketId: number) => {
    setSelectedTickets((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map((t) => t.id));
    }
  };

  const handleBulkUpdate = async (data: {
    status?: TicketStatus;
    priority?: TicketPriority;
    assigned_to?: number;
  }) => {
    if (selectedTickets.length === 0) {
      alert(t('ticket_management.bulk_actions.select_one_alert'));
      return;
    }

    try {
      await bulkUpdateTickets({
        ticket_ids: selectedTickets,
        ...data,
      });

      setSelectedTickets([]);
      setShowBulkActions(false);
      await loadTickets();
    } catch (err: any) {
      setError(err.response?.data?.message || t('ticket_management.messages.update_error'));
      console.error('Error updating tickets:', err);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('ticket_management.page_title')}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {t('ticket_management.page_subtitle')}
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">{t('ticket_management.filters.title')}</h3>
          <button
            onClick={() => {
              setFilters({
                status: undefined,
                category: undefined,
                priority: undefined,
                assigned_to: undefined,
                search: '',
                sort_by: 'created_at',
                sort_order: 'desc',
                per_page: 20,
                page: 1,
              });
            }}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t('ticket_management.filters.clear_button')}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="sm:col-span-2 lg:col-span-2">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              {t('ticket_management.filters.search_label')}
            </label>
            <input
              id="search"
              type="text"
              placeholder={t('ticket_management.filters.search_placeholder')}
              className="input"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('ticket_management.filters.status_label')}
            </label>
            <select
              id="status-filter"
              className="input"
              value={filters.status || ''}
              onChange={(e) =>
                handleFilterChange('status', (e.target.value as TicketStatus) || undefined)
              }
            >
              <option value="">{t('ticket_management.filters.all_statuses')}</option>
              <option value="open">{t('ticket_management.status.open')}</option>
              <option value="in_progress">{t('ticket_management.status.in_progress')}</option>
              <option value="waiting_user">{t('ticket_management.status.waiting_user')}</option>
              <option value="resolved">{t('ticket_management.status.resolved')}</option>
              <option value="closed">{t('ticket_management.status.closed')}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label htmlFor="category-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('ticket_management.filters.category_label')}
            </label>
            <select
              id="category-filter"
              className="input"
              value={filters.category || ''}
              onChange={(e) =>
                handleFilterChange('category', (e.target.value as TicketCategory) || undefined)
              }
            >
              <option value="">{t('ticket_management.filters.all_categories')}</option>
              <option value="technical">{t('ticket_management.category.technical')}</option>
              <option value="billing">{t('ticket_management.category.billing')}</option>
              <option value="feature_request">{t('ticket_management.category.feature_request')}</option>
              <option value="bug_report">{t('ticket_management.category.bug_report')}</option>
              <option value="account">{t('ticket_management.category.account')}</option>
              <option value="other">{t('ticket_management.category.other')}</option>
            </select>
          </div>

          {/* Priority Filter */}
          <div>
            <label htmlFor="priority-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('ticket_management.filters.priority_label')}
            </label>
            <select
              id="priority-filter"
              className="input"
              value={filters.priority || ''}
              onChange={(e) =>
                handleFilterChange('priority', (e.target.value as TicketPriority) || undefined)
              }
            >
              <option value="">{t('ticket_management.filters.all_priorities')}</option>
              <option value="low">{t('ticket_management.priority.low')}</option>
              <option value="medium">{t('ticket_management.priority.medium')}</option>
              <option value="high">{t('ticket_management.priority.high')}</option>
              <option value="urgent">{t('ticket_management.priority.urgent')}</option>
            </select>
          </div>

          {/* Assigned Admin Filter */}
          <div>
            <label htmlFor="admin-filter" className="block text-sm font-medium text-gray-700 mb-1">
              {t('ticket_management.filters.assigned_label')}
            </label>
            <select
              id="admin-filter"
              className="input"
              value={filters.assigned_to?.toString() || ''}
              onChange={(e) =>
                handleFilterChange(
                  'assigned_to',
                  e.target.value === 'unassigned'
                    ? 'unassigned'
                    : e.target.value
                      ? Number(e.target.value)
                      : undefined
                )
              }
            >
              <option value="">{t('ticket_management.filters.all_admins')}</option>
              <option value="unassigned">{t('ticket_management.filters.unassigned')}</option>
              {admins.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => {
              setFilters({
                status: undefined,
                category: undefined,
                priority: undefined,
                assigned_to: undefined,
                search: '',
                sort_by: 'created_at',
                sort_order: 'desc',
                per_page: 20,
                page: 1,
              });
            }}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            {t('ticket_management.filters.clear_all_button')}
          </button>

          <p className="text-sm font-medium text-gray-700">
            {pagination.total} {pagination.total === 1 ? t('ticket_management.pagination.ticket_found') : t('ticket_management.pagination.tickets_found')}
          </p>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedTickets.length > 0 && (
        <div className="card mb-6 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-blue-900">
              {selectedTickets.length} {selectedTickets.length !== 1 ? t('ticket_management.bulk_actions.tickets') : t('ticket_management.bulk_actions.ticket')} {t('ticket_management.bulk_actions.selected')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="btn btn-secondary text-sm"
              >
                {t('ticket_management.bulk_actions.bulk_actions_button')}
              </button>
              <button
                onClick={() => setSelectedTickets([])}
                className="btn btn-secondary text-sm"
              >
                {t('ticket_management.bulk_actions.clear_selection_button')}
              </button>
            </div>
          </div>

          {showBulkActions && (
            <div className="mt-4 pt-4 border-t border-blue-200 grid grid-cols-3 gap-3">
              <div>
                <label className="label text-sm">{t('ticket_management.bulk_actions.set_status_label')}</label>
                <select
                  className="input"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate({ status: e.target.value as TicketStatus });
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">{t('ticket_management.bulk_actions.choose_status')}</option>
                  <option value="open">{t('ticket_management.status.open')}</option>
                  <option value="in_progress">{t('ticket_management.status.in_progress')}</option>
                  <option value="waiting_user">{t('ticket_management.status.waiting_user')}</option>
                  <option value="resolved">{t('ticket_management.status.resolved')}</option>
                  <option value="closed">{t('ticket_management.status.closed')}</option>
                </select>
              </div>

              <div>
                <label className="label text-sm">{t('ticket_management.bulk_actions.set_priority_label')}</label>
                <select
                  className="input"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate({ priority: e.target.value as TicketPriority });
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">{t('ticket_management.bulk_actions.choose_priority')}</option>
                  <option value="low">{t('ticket_management.priority.low')}</option>
                  <option value="medium">{t('ticket_management.priority.medium')}</option>
                  <option value="high">{t('ticket_management.priority.high')}</option>
                  <option value="urgent">{t('ticket_management.priority.urgent')}</option>
                </select>
              </div>

              <div>
                <label className="label text-sm">{t('ticket_management.bulk_actions.assign_to_label')}</label>
                <select
                  className="input"
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkUpdate({ assigned_to: Number(e.target.value) });
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">{t('ticket_management.bulk_actions.choose_admin')}</option>
                  {admins.map((admin) => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Ticket Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedTickets.length === tickets.length && tickets.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.ticket')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.priority')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.assigned')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.created')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('ticket_management.table.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    {t('ticket_management.messages.loading')}
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    {t('ticket_management.messages.no_tickets')}
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTickets.includes(ticket.id)}
                        onChange={() => handleSelectTicket(ticket.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/tickets/${ticket.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <div className="text-sm font-medium">{ticket.ticket_number}</div>
                        <div className="text-sm text-gray-900 truncate max-w-xs">
                          {ticket.subject}
                        </div>
                        <div className="text-xs text-gray-500">
                          {categoryLabels[ticket.category]}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-block px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusColors[ticket.status]}`}
                      >
                        {statusLabels[ticket.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-sm font-medium ${priorityColors[ticket.priority]}`}>
                        {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ticket.assignedAdmin ? ticket.assignedAdmin.name : t('ticket_management.filters.unassigned')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {ticket.user?.name}
                      <div className="text-xs text-gray-500">{ticket.account?.name}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(ticket.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right text-sm">
                      <Link
                        to={`/admin/tickets/${ticket.id}`}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {t('ticket_management.table.view')}
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {!isLoading && pagination.lastPage > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <button
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('ticket_management.pagination.previous')}
          </button>

          <span className="text-sm text-gray-700">
            {t('ticket_management.pagination.page')} {pagination.currentPage} {t('ticket_management.pagination.of')} {pagination.lastPage}
          </span>

          <button
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.lastPage}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('ticket_management.pagination.next')}
          </button>
        </div>
      )}
    </div>
  );
}
